import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    User,
    Mail,
    Phone,
    Building2,
    MapPin,
    FileText,
    Upload,
    X,
    ArrowLeft,
    Car,
    Home,
    CheckCircle,
    AlertCircle,
    Eye,
    EyeOff,
    ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useRegisterDealerMutation } from '../../store/slices/apiSlice';
import DiasporawiseLogo from '../../assets/DWBRANDblue.png';

const registrationSchema = yup.object({
    name: yup.string().required('Name is required'),
    email: yup.string().email('Invalid email').required('Email is required'),
    mobile_number: yup.string().optional(),
    password: yup.string().min(8, 'Password must be at least 8 characters').optional(),
    business_name: yup.string().required('Business name is required'),
    business_address: yup.string().optional(),
    business_phone: yup.string().optional(),
    dealer_type: yup.string().oneOf(['car', 'house'], 'Invalid dealer type').required('Dealer type is required'),
    country_code: yup.string().optional(),
    currency_symbol: yup.string().optional(),
});

type RegistrationFormData = yup.InferType<typeof registrationSchema>;

interface DocumentFile {
    file: File;
    preview: string;
}

const DealerRegistration: React.FC = () => {
    const navigate = useNavigate();
    const [registerDealer, { isLoading }] = useRegisterDealerMutation();
    const locationRef = React.useRef<HTMLDivElement>(null);
    const currencyRef = React.useRef<HTMLDivElement>(null);
    const countryCodeRef = React.useRef<HTMLDivElement>(null);
    
    const [showPassword, setShowPassword] = useState(false);
    const [businessAddress, setBusinessAddress] = useState('');
    const [locationSearch, setLocationSearch] = useState('');
    const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
    const [locationSuggestions, setLocationSuggestions] = useState<any[]>([]);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
    const [showCountryCodeDropdown, setShowCountryCodeDropdown] = useState(false);
    
    const [documents, setDocuments] = useState<{
        business_license?: DocumentFile;
        id_proof?: DocumentFile;
        address_proof?: DocumentFile;
        tax_certificate?: DocumentFile;
        other: DocumentFile[];
    }>({
        other: [],
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useForm<RegistrationFormData>({
        resolver: yupResolver(registrationSchema),
        defaultValues: {
            dealer_type: 'car',
        },
    });

    const dealerType = watch('dealer_type');

    // Location search function
    const searchLocation = async (query: string) => {
        if (!query || query.length < 3) {
            setLocationSuggestions([]);
            setShowLocationSuggestions(false);
            return;
        }

        setIsSearchingLocation(true);
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(query)}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}`
            );
            const data = await response.json();
            
            if (data.predictions) {
                setLocationSuggestions(data.predictions);
                setShowLocationSuggestions(true);
            } else {
                setLocationSuggestions([]);
                setShowLocationSuggestions(false);
            }
        } catch (error: any) {
            console.error('Location search error:', error);
            setLocationSuggestions([]);
            setShowLocationSuggestions(false);
        } finally {
            setIsSearchingLocation(false);
        }
    };

    // Handle location input change with debounce
    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (locationSearch) {
                searchLocation(locationSearch);
            } else {
                setLocationSuggestions([]);
                setShowLocationSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [locationSearch]);

    // Handle location selection
    const selectLocation = (location: any) => {
        const address = location.description || location.structured_formatting?.main_text || '';
        setLocationSearch(address);
        setBusinessAddress(address);
        setValue('business_address', address);
        setShowLocationSuggestions(false);
    };

    // Close location suggestions when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
                setShowLocationSuggestions(false);
            }
            if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
                setShowCurrencyDropdown(false);
            }
            if (countryCodeRef.current && !countryCodeRef.current.contains(event.target as Node)) {
                setShowCountryCodeDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Currency symbols list
    const currencySymbols = [
        { symbol: '₦', code: 'NGN', name: 'Nigerian Naira' },
        { symbol: '$', code: 'USD', name: 'US Dollar' },
        { symbol: '€', code: 'EUR', name: 'Euro' },
        { symbol: '£', code: 'GBP', name: 'British Pound' },
        { symbol: '¥', code: 'JPY', name: 'Japanese Yen' },
        { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
        { symbol: '₨', code: 'PKR', name: 'Pakistani Rupee' },
        { symbol: 'R$', code: 'BRL', name: 'Brazilian Real' },
        { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar' },
        { symbol: 'A$', code: 'AUD', name: 'Australian Dollar' },
        { symbol: 'CHF', code: 'CHF', name: 'Swiss Franc' },
        { symbol: '¥', code: 'CNY', name: 'Chinese Yuan' },
        { symbol: '₩', code: 'KRW', name: 'South Korean Won' },
        { symbol: '₽', code: 'RUB', name: 'Russian Ruble' },
        { symbol: 'R', code: 'ZAR', name: 'South African Rand' },
    ];

    // Country codes list
    const countryCodes = [
        { code: 'US', name: 'United States' },
        { code: 'NG', name: 'Nigeria' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'CA', name: 'Canada' },
        { code: 'AU', name: 'Australia' },
        { code: 'IN', name: 'India' },
        { code: 'PK', name: 'Pakistan' },
        { code: 'BR', name: 'Brazil' },
        { code: 'DE', name: 'Germany' },
        { code: 'FR', name: 'France' },
        { code: 'IT', name: 'Italy' },
        { code: 'ES', name: 'Spain' },
        { code: 'NL', name: 'Netherlands' },
        { code: 'BE', name: 'Belgium' },
        { code: 'CH', name: 'Switzerland' },
        { code: 'AT', name: 'Austria' },
        { code: 'SE', name: 'Sweden' },
        { code: 'NO', name: 'Norway' },
        { code: 'DK', name: 'Denmark' },
        { code: 'FI', name: 'Finland' },
        { code: 'PL', name: 'Poland' },
        { code: 'IE', name: 'Ireland' },
        { code: 'PT', name: 'Portugal' },
        { code: 'GR', name: 'Greece' },
        { code: 'ZA', name: 'South Africa' },
        { code: 'KE', name: 'Kenya' },
        { code: 'GH', name: 'Ghana' },
        { code: 'EG', name: 'Egypt' },
        { code: 'AE', name: 'United Arab Emirates' },
        { code: 'SA', name: 'Saudi Arabia' },
        { code: 'JP', name: 'Japan' },
        { code: 'CN', name: 'China' },
        { code: 'KR', name: 'South Korea' },
        { code: 'SG', name: 'Singapore' },
        { code: 'MY', name: 'Malaysia' },
        { code: 'TH', name: 'Thailand' },
        { code: 'ID', name: 'Indonesia' },
        { code: 'PH', name: 'Philippines' },
        { code: 'VN', name: 'Vietnam' },
        { code: 'NZ', name: 'New Zealand' },
    ];

    const currencySymbol = watch('currency_symbol');
    const countryCode = watch('country_code');
    const selectedCurrency = currencySymbol ? currencySymbols.find(c => c.symbol === currencySymbol) : null;
    const selectedCountryCode = countryCode ? countryCodes.find(c => c.code === countryCode) : null;

    const handleDocumentUpload = (type: 'business_license' | 'id_proof' | 'address_proof' | 'tax_certificate' | 'other', file: File) => {
        const preview = URL.createObjectURL(file);
        
        if (type === 'other') {
            setDocuments(prev => ({
                ...prev,
                other: [...prev.other, { file, preview }],
            }));
        } else {
            setDocuments(prev => ({
                ...prev,
                [type]: { file, preview },
            }));
        }
    };

    const removeDocument = (type: 'business_license' | 'id_proof' | 'address_proof' | 'tax_certificate' | 'other', index?: number) => {
        if (type === 'other' && index !== undefined) {
            setDocuments(prev => ({
                ...prev,
                other: prev.other.filter((_, i) => i !== index),
            }));
        } else {
            setDocuments(prev => {
                const newDocs = { ...prev };
                delete newDocs[type];
                return newDocs;
            });
        }
    };

    const onSubmit = async (data: RegistrationFormData) => {
        try {
            const formData = new FormData();
            
            // Add text fields
            formData.append('name', data.name);
            formData.append('email', data.email);
            if (data.mobile_number) formData.append('mobile_number', data.mobile_number);
            if (data.password) formData.append('password', data.password);
            formData.append('business_name', data.business_name);
            if (data.business_address) formData.append('business_address', data.business_address);
            if (data.business_phone) formData.append('business_phone', data.business_phone);
            formData.append('dealer_type', data.dealer_type);
            if (data.country_code) formData.append('country_code', data.country_code);
            if (data.currency_symbol) formData.append('currency_symbol', data.currency_symbol);

            // Add documents
            if (documents.business_license) {
                formData.append('business_license', documents.business_license.file);
            }
            if (documents.id_proof) {
                formData.append('id_proof', documents.id_proof.file);
            }
            if (documents.address_proof) {
                formData.append('address_proof', documents.address_proof.file);
            }
            if (documents.tax_certificate) {
                formData.append('tax_certificate', documents.tax_certificate.file);
            }
            documents.other.forEach((doc) => {
                formData.append('other', doc.file);
            });

            const response = await registerDealer(formData).unwrap();
            
            if (response.success) {
                toast.success('Registration successful! Please wait for admin verification.');
                window.location.href = 'https://app.diasporawise.com';
            }
        } catch (error: any) {
            toast.error(error?.data?.message || 'Registration failed. Please try again.');
            console.error('Registration error:', error);
        }
    };

    const DocumentUploadField: React.FC<{
        label: string;
        type: 'business_license' | 'id_proof' | 'address_proof' | 'tax_certificate' | 'other';
        required?: boolean;
        multiple?: boolean;
    }> = ({ label, type, required = false, multiple = false }) => {
        const doc = type === 'other' ? null : documents[type];
        const otherDocs = type === 'other' ? documents.other : [];

        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
                {type === 'other' ? (
                    <div className="space-y-2">
                        {otherDocs.map((doc, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                <FileText className="w-4 h-4 text-gray-500" />
                                <span className="flex-1 text-sm text-gray-700 truncate">{doc.file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => removeDocument(type, index)}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        <label className="flex items-center justify-center w-full p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                            <Upload className="w-5 h-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">Upload {label}</span>
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*,.pdf"
                                multiple={multiple}
                                onChange={(e) => {
                                    if (e.target.files) {
                                        Array.from(e.target.files).forEach(file => {
                                            handleDocumentUpload(type, file);
                                        });
                                    }
                                }}
                            />
                        </label>
                    </div>
                ) : (
                    <div>
                        {doc ? (
                            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                <FileText className="w-4 h-4 text-gray-500" />
                                <span className="flex-1 text-sm text-gray-700 truncate">{doc.file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => removeDocument(type)}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <label className="flex items-center justify-center w-full p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                                <Upload className="w-5 h-5 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-600">Upload {label}</span>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*,.pdf"
                                    onChange={(e) => {
                                        if (e.target.files?.[0]) {
                                            handleDocumentUpload(type, e.target.files[0]);
                                        }
                                    }}
                                />
                            </label>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-white py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            <span className="text-sm font-medium">Back</span>
                        </button>
                        <div className="flex-1"></div>
                    </div>

                    <Link to="/" className="inline-block mb-4">
                        <div className="flex items-center justify-center">
                            <img
                                src={DiasporawiseLogo}
                                alt="Diasporawise Logo"
                                className="h-12 w-auto object-contain mr-3"
                            />
                            <div>
                                <h1 className="text-3xl font-bold text-red-600">Diasporawise</h1>
                                <p className="text-sm text-gray-600">Dealer Registration</p>
                            </div>
                        </div>
                    </Link>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Become a Dealer</h2>
                    <p className="text-gray-600">Register your business and start listing</p>
                </div>

                {/* Registration Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow-md p-8 space-y-6">
                    {/* Personal Information */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <User className="w-5 h-5" />
                            Personal Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Name *
                                </label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        {...register('name')}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter your full name"
                                    />
                                </div>
                                {errors.name && (
                                    <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Address *
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="email"
                                        {...register('email')}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="your@email.com"
                                    />
                                </div>
                                {errors.email && (
                                    <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Mobile Number
                                </label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="tel"
                                        {...register('mobile_number')}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="+1234567890"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Password (Optional)
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        {...register('password')}
                                        className="w-full px-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Min 8 characters"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-5 h-5" />
                                        ) : (
                                            <Eye className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Business Information */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Building2 className="w-5 h-5" />
                            Business Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Business Name *
                                </label>
                                <div className="relative">
                                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        {...register('business_name')}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Your Business Name"
                                    />
                                </div>
                                {errors.business_name && (
                                    <p className="text-xs text-red-500 mt-1">{errors.business_name.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Business Phone
                                </label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="tel"
                                        {...register('business_phone')}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="+1234567890"
                                    />
                                </div>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Business Address
                                </label>
                                <div className="relative" ref={locationRef}>
                                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                                    <input
                                        type="text"
                                        value={locationSearch}
                                        onChange={(e) => {
                                            setLocationSearch(e.target.value);
                                            setBusinessAddress(e.target.value);
                                            setValue('business_address', e.target.value);
                                        }}
                                        onFocus={() => {
                                            if (locationSuggestions.length > 0) {
                                                setShowLocationSuggestions(true);
                                            }
                                        }}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Search for a location (e.g., Lagos, London, New York)"
                                    />
                                    {isSearchingLocation && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                        </div>
                                    )}
                                    
                                    {/* Location Suggestions Dropdown */}
                                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {locationSuggestions.map((location, index) => (
                                                <button
                                                    key={location.place_id || index}
                                                    type="button"
                                                    onClick={() => selectLocation(location)}
                                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                                >
                                                    <div className="flex items-start">
                                                        <MapPin className="w-4 h-4 text-gray-400 mt-1 mr-2 flex-shrink-0" />
                                                        <div className="flex-1">
                                                            <div className="font-medium text-gray-900">
                                                                {location.structured_formatting?.main_text || location.description}
                                                            </div>
                                                            {location.structured_formatting?.secondary_text && (
                                                                <div className="text-sm text-gray-500 mt-0.5">
                                                                    {location.structured_formatting.secondary_text}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {businessAddress && (
                                    <p className="mt-1 text-xs text-gray-500">
                                        Selected: {businessAddress}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Dealer Type *
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                        dealerType === 'car'
                                            ? 'border-gray-400 bg-white text-gray-900'
                                            : 'border-gray-300 hover:border-gray-400 bg-white text-gray-600'
                                    }`}>
                                        <input
                                            type="radio"
                                            {...register('dealer_type')}
                                            value="car"
                                            className="hidden"
                                        />
                                        <div className="text-center">
                                            <Car className={`w-6 h-6 mx-auto mb-2 ${dealerType === 'car' ? 'text-gray-900' : 'text-gray-400'}`} />
                                            <span className="text-sm font-medium">Car Dealer</span>
                                        </div>
                                    </label>
                                    <label className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                        dealerType === 'house'
                                            ? 'border-gray-400 bg-white text-gray-900'
                                            : 'border-gray-300 hover:border-gray-400 bg-white text-gray-600'
                                    }`}>
                                        <input
                                            type="radio"
                                            {...register('dealer_type')}
                                            value="house"
                                            className="hidden"
                                        />
                                        <div className="text-center">
                                            <Home className={`w-6 h-6 mx-auto mb-2 ${dealerType === 'house' ? 'text-gray-900' : 'text-gray-400'}`} />
                                            <span className="text-sm font-medium">House Dealer</span>
                                        </div>
                                    </label>
                                </div>
                                {errors.dealer_type && (
                                    <p className="text-xs text-red-500 mt-1">{errors.dealer_type.message}</p>
                                )}
                            </div>

                            <div ref={countryCodeRef} className="relative">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Country Code
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setShowCountryCodeDropdown(!showCountryCodeDropdown)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between hover:bg-gray-50"
                                >
                                    <span className="text-gray-900">
                                        {selectedCountryCode ? `${selectedCountryCode.code} - ${selectedCountryCode.name}` : 'Select Country Code'}
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showCountryCodeDropdown ? 'transform rotate-180' : ''}`} />
                                </button>
                                
                                {showCountryCodeDropdown && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {countryCodes.map((country, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => {
                                                    setValue('country_code', country.code);
                                                    setShowCountryCodeDropdown(false);
                                                }}
                                                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {country.code} - {country.name}
                                                        </div>
                                                    </div>
                                                    {watch('country_code') === country.code && (
                                                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div ref={currencyRef} className="relative">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Currency Symbol
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between hover:bg-gray-50"
                                >
                                    <span className="text-gray-900">
                                        {selectedCurrency ? `${selectedCurrency.symbol} - ${selectedCurrency.name}` : 'Select Currency'}
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showCurrencyDropdown ? 'transform rotate-180' : ''}`} />
                                </button>
                                
                                {showCurrencyDropdown && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {currencySymbols.map((currency, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => {
                                                    setValue('currency_symbol', currency.symbol);
                                                    setShowCurrencyDropdown(false);
                                                }}
                                                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {currency.symbol} - {currency.name}
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-0.5">
                                                            {currency.code}
                                                        </div>
                                                    </div>
                                                    {watch('currency_symbol') === currency.symbol && (
                                                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Documents */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Required Documents
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <DocumentUploadField label="Business License" type="business_license" required />
                            <DocumentUploadField label="ID Proof" type="id_proof" required />
                            <DocumentUploadField label="Address Proof" type="address_proof" required />
                            <DocumentUploadField label="Tax Certificate" type="tax_certificate" required />
                            <div className="md:col-span-2">
                                <DocumentUploadField label="Other Documents" type="other" multiple />
                            </div>
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-gray-600 mt-0.5" />
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-1">Verification Process</h4>
                                <p className="text-xs text-gray-700">
                                    After registration, your account will be reviewed by our admin team. 
                                    You'll receive an email notification once your account is approved. 
                                    You can view your profile but cannot create listings until approved.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex gap-4 pt-4">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Registering...</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-5 h-5" />
                                    <span>Register as Dealer</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Login Link */}
                    <div className="text-center pt-4 border-t border-gray-200 lg:hidden">
                        <p className="text-sm text-gray-600">
                            Already have an account?{' '}
                            <a href="https://app.diasporawise.com" className="text-blue-600 hover:text-blue-700 font-medium">
                                Login here
                            </a>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DealerRegistration;
